import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Calculator, Info, TrendingDown, ArrowRight, Settings as SettingsIcon,
  Loader2, Wallet, ChevronDown, ChevronUp, RotateCcw,
} from "lucide-react";

/**
 * Tax calculator for Italian hair salons.
 *
 * Regole 2025 semplificate — aggiornate dopo feedback utente:
 *  • INPS Gestione Artigiani ora modella correttamente:
 *      - contributo fisso sul minimale (€18.415 → ~€4.427,04/anno anche se il
 *        reddito è più basso — è la "quota fissa" che ogni artigiano paga)
 *      - aliquota 24% su eccedenza fino al massimale (€55.008)
 *      - aliquota 25% oltre il massimale fino al secondo massimale (€120.607)
 *      - opzionale riduzione 35% per forfettari (art. 1 c. 77 L. 190/2014)
 *  • Ordinario semplificato: aggiunge addizionali IRPEF regionale + comunale.
 *  • Tutte le costanti sono modificabili dall'utente (persistite in localStorage)
 *    così può adeguarle alla propria realtà (regione, gestione INPS specifica,
 *    aggiornamenti annuali).
 */

const STORAGE_KEY = "taxcalc_settings_v2";

// Costanti INPS Gestione Artigiani 2025 (Circolare INPS n. 22/2025)
const DEFAULT_SETTINGS = {
  // INPS Gestione Artigiani
  artigiano_iscritto: true,
  inps_minimale_annuo: 18415,
  inps_fisso_annuo: 4427.04,
  inps_aliquota: 24,
  inps_massimale_annuo: 55008,
  inps_aliquota_extra: 25,
  inps_secondo_massimale: 120607,
  forfettario_riduzione_35: false,
  forfettario_deduci_inps: true,        // INPS deducibile dall'imponibile forfettario (art. 1 c. 64 L. 190/2014)
  // Fiscali
  addizionale_regionale_pct: 1.73,
  addizionale_comunale_pct: 0.80,
  detrazione_lav_autonomo: 0,
  iva_aliquota: 22,
  // IRAP
  ord_autonoma_organizzazione: false,   // Ordinario: IRAP dovuta solo se sopra soglia (dipendenti/beni strumentali)
  irap_aliquota: 3.9,                   // % (varia per regione)
  srl_personale_annuo: 0,               // SRL: costo lavoro dipendente (base IRAP)
  // SRL
  ires_aliquota: 24,
  srl_distribuzione_utili_pct: 0,       // % utile distribuito ai soci
  srl_dividendi_aliquota: 26,           // ritenuta secca dividendi (partecipazione qualificata o meno)
  // Forfettario soglia uscita
  forfettario_soglia_ricavi: 85000,
};

export default function TaxCalculator() {
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [revenue, setRevenue] = useState("");
  const [costs, setCosts] = useState("");
  const [period, setPeriod] = useState("month");
  const [autoLoad, setAutoLoad] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? { ...DEFAULT_SETTINGS, ...JSON.parse(raw) } : DEFAULT_SETTINGS;
    } catch { return DEFAULT_SETTINGS; }
  });

  useEffect(() => { localStorage.setItem(STORAGE_KEY, JSON.stringify(settings)); }, [settings]);
  useEffect(() => {
    api.get("/tenant").then((r) => setTenant(r.data)).finally(() => setLoading(false));
  }, []);

  const fetchRealData = async () => {
    setAutoLoad(true);
    try {
      const [inv, cost] = await Promise.all([api.get("/invoices"), api.get("/costs")]);
      const now = new Date();
      const start = periodStart(period, now);
      const invSum = inv.data
        .filter((i) => new Date(i.date || i.created_at) >= start && (i.status === "paid" || !i.status))
        .reduce((s, i) => s + Number(i.total || 0), 0);
      const costSum = (cost.data || [])
        .filter((c) => new Date(c.date || c.created_at) >= start)
        .reduce((s, c) => s + Number(c.amount || 0), 0);
      setRevenue(invSum.toFixed(2));
      setCosts(costSum.toFixed(2));
      toast.success(`Caricati ${inv.data.length} incassi e ${cost.data.length} costi del periodo`);
    } catch (e) { toast.error("Errore nel recupero dati"); }
    finally { setAutoLoad(false); }
  };

  const rev = Number(revenue || 0);
  const cst = Number(costs || 0);
  const regime = tenant?.tax_regime;
  const coef = tenant?.ateco_coefficient ?? 0.67;
  const isForfettario = regime === "forfettario_5" || regime === "forfettario_15";
  const isSrl = regime === "ordinario_srl";

  const result = useMemo(
    () => (regime ? computeTaxes(regime, rev, cst, coef, period, settings) : null),
    [regime, rev, cst, coef, period, settings],
  );

  if (loading) return (
    <div className="sp-card p-16 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-[color:var(--sp-text-muted)]" /></div>
  );

  return (
    <div data-testid="tax-calculator-page" className="space-y-8">
      <header>
        <h1 className="font-display text-4xl font-black tracking-tight inline-flex items-center gap-3">
          <Calculator className="h-8 w-8 text-[color:var(--sp-brand)]" /> Calcolatore Tasse
        </h1>
        <p className="text-[color:var(--sp-text-secondary)] mt-2 max-w-2xl">
          Stima realistica di <strong>imposte + contributi</strong> in base al <strong>regime fiscale</strong>
          e alla <strong>gestione INPS artigiani</strong>. Include contributo minimo, addizionali IRPEF,
          e tutti i massimali 2025.
        </p>
      </header>

      {!regime ? (
        <div className="sp-card p-8 border-2 border-dashed border-amber-300 bg-amber-50/40">
          <div className="flex items-start gap-4">
            <div className="h-10 w-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
              <Info className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <div className="font-display text-lg font-black">Imposta prima il regime fiscale</div>
              <p className="text-sm text-[color:var(--sp-text-secondary)] mt-1">
                Vai su <strong>Impostazioni → Dati fiscali</strong> e seleziona il regime del tuo
                salone (Forfettario, Ordinario, SRL, ecc.). Poi torna qui.
              </p>
              <Link to="/app/settings#fiscal" className="sp-btn-primary mt-3 inline-flex items-center gap-2 text-sm" data-testid="go-settings-fiscal">
                <SettingsIcon className="h-4 w-4" /> Vai ai dati fiscali <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="sp-card p-5 flex items-center gap-4 flex-wrap">
            <div className="h-10 w-10 rounded-xl bg-[color:var(--sp-brand)]/10 text-[color:var(--sp-brand)] flex items-center justify-center">
              <Wallet className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-[200px]">
              <div className="text-[10px] font-bold uppercase tracking-wider text-[color:var(--sp-text-muted)]">Regime attivo</div>
              <div className="font-display text-lg font-black">{REGIME_LABELS[regime] || regime}</div>
            </div>
            <Link to="/app/settings#fiscal" className="sp-btn-ghost text-xs" data-testid="change-regime-link">Cambia regime</Link>
          </div>

          {/* Impostazioni contributive avanzate — accordion */}
          <div className="sp-card" data-testid="tc-settings-card">
            <button type="button" onClick={() => setShowSettings((v) => !v)}
              data-testid="tc-settings-toggle"
              className="w-full p-5 flex items-center justify-between gap-4 hover:bg-[color:var(--sp-subtle)]/40 transition-colors">
              <div className="flex items-center gap-3">
                <span className="h-9 w-9 rounded-xl bg-[color:var(--sp-brand)]/10 text-[color:var(--sp-brand)] flex items-center justify-center">
                  <SettingsIcon className="h-4 w-4" />
                </span>
                <div className="text-left">
                  <div className="font-display font-black">Impostazioni contributive avanzate</div>
                  <div className="text-[11px] text-[color:var(--sp-text-muted)]">
                    Minimali INPS artigiani, aliquote, addizionali IRPEF. Default 2025.
                  </div>
                </div>
              </div>
              {showSettings ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
            </button>
            {showSettings && (
              <div className="border-t border-[color:var(--sp-border)] p-6 space-y-6" data-testid="tc-settings-body">
                {!isSrl && (
                  <label className="flex items-start gap-3 p-3 rounded-xl bg-[color:var(--sp-subtle)]/50 cursor-pointer">
                    <input type="checkbox" className="h-4 w-4 mt-0.5" checked={settings.artigiano_iscritto}
                      data-testid="tc-artigiano-iscritto"
                      onChange={(e) => setSettings({ ...settings, artigiano_iscritto: e.target.checked })} />
                    <div>
                      <div className="text-sm font-bold">Sono iscritto alla Gestione Artigiani INPS</div>
                      <div className="text-[11px] text-[color:var(--sp-text-muted)]">
                        Come parrucchiere titolare di ditta individuale/impresa artigiana di norma sì. Se sei co.co.co, socio non lavoratore o altro, disattiva per escludere contributi artigiani.
                      </div>
                    </div>
                  </label>
                )}

                {settings.artigiano_iscritto && !isSrl && (
                  <div>
                    <div className="text-xs font-bold uppercase tracking-wider text-[color:var(--sp-text-muted)] mb-3">
                      INPS Gestione Artigiani (annuo)
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                      <NumField label="Reddito minimale imponibile (€)"
                        value={settings.inps_minimale_annuo} onChange={(v) => setSettings({ ...settings, inps_minimale_annuo: v })}
                        tid="tc-inps-minimale"
                        help="Sotto questo importo si paga solo il contributo fisso (2025 ≈ 18.415)" />
                      <NumField label="Contributo fisso annuo (€)"
                        value={settings.inps_fisso_annuo} onChange={(v) => setSettings({ ...settings, inps_fisso_annuo: v })}
                        tid="tc-inps-fisso"
                        help="Dovuto sempre, anche a reddito basso. 2025 ≈ 4.427,04 (artigiano titolare)" />
                      <NumField label="Aliquota % oltre minimale"
                        value={settings.inps_aliquota} onChange={(v) => setSettings({ ...settings, inps_aliquota: v })}
                        tid="tc-inps-aliquota" step="0.1" suffix="%" />
                      <NumField label="Primo massimale (€)"
                        value={settings.inps_massimale_annuo} onChange={(v) => setSettings({ ...settings, inps_massimale_annuo: v })}
                        tid="tc-inps-massimale" help="2025 ≈ 55.008" />
                      <NumField label="Aliquota % oltre 1° massimale"
                        value={settings.inps_aliquota_extra} onChange={(v) => setSettings({ ...settings, inps_aliquota_extra: v })}
                        tid="tc-inps-aliq-extra" step="0.1" suffix="%"
                        help="Per iscritti dopo 1° gennaio 1996" />
                      <NumField label="Secondo massimale (€)"
                        value={settings.inps_secondo_massimale} onChange={(v) => setSettings({ ...settings, inps_secondo_massimale: v })}
                        tid="tc-inps-massimale2" help="2025 ≈ 120.607 — sopra non si paga più" />
                    </div>
                    {isForfettario && (
                      <label className="mt-4 flex items-start gap-3 p-3 rounded-xl bg-emerald-50 border border-emerald-200 cursor-pointer">
                        <input type="checkbox" className="h-4 w-4 mt-0.5" checked={settings.forfettario_riduzione_35}
                          data-testid="tc-forfett-red35"
                          onChange={(e) => setSettings({ ...settings, forfettario_riduzione_35: e.target.checked })} />
                        <div>
                          <div className="text-sm font-bold text-emerald-900">Applica riduzione 35% contributi (forfettario)</div>
                          <div className="text-[11px] text-emerald-800">
                            Opzione facoltativa per iscritti alla gestione artigiani in regime forfettario (art. 1 c. 77 L. 190/2014). Riduce del 35% sia il contributo fisso sia quello variabile.
                          </div>
                        </div>
                      </label>
                    )}
                  </div>
                )}

                {isForfettario && (
                  <label className="flex items-start gap-3 p-3 rounded-xl bg-blue-50 border border-blue-200 cursor-pointer">
                    <input type="checkbox" className="h-4 w-4 mt-0.5" checked={settings.forfettario_deduci_inps}
                      data-testid="tc-forfett-deduci-inps"
                      onChange={(e) => setSettings({ ...settings, forfettario_deduci_inps: e.target.checked })} />
                    <div>
                      <div className="text-sm font-bold text-blue-900">Deduci contributi INPS dall'imponibile forfettario</div>
                      <div className="text-[11px] text-blue-800">
                        L'art. 1 c. 64 L. 190/2014 consente di sottrarre i contributi previdenziali obbligatori dall'imponibile prima di applicare l'imposta sostitutiva. Attivo di default: se lasci disattivato paghi imposta sostitutiva anche sui contributi (più tasse).
                      </div>
                    </div>
                  </label>
                )}

                {!isForfettario && !isSrl && (
                  <label className="flex items-start gap-3 p-3 rounded-xl bg-amber-50 border border-amber-200 cursor-pointer">
                    <input type="checkbox" className="h-4 w-4 mt-0.5" checked={settings.ord_autonoma_organizzazione}
                      data-testid="tc-autonoma-organizzazione"
                      onChange={(e) => setSettings({ ...settings, ord_autonoma_organizzazione: e.target.checked })} />
                    <div>
                      <div className="text-sm font-bold text-amber-900">Ho autonoma organizzazione (IRAP dovuta)</div>
                      <div className="text-[11px] text-amber-800">
                        Attiva se hai dipendenti sopra soglia o beni strumentali eccedenti quelli minimi. In caso contrario il parrucchiere individuale è esente IRAP.
                      </div>
                    </div>
                  </label>
                )}
                {!isForfettario && !isSrl && settings.ord_autonoma_organizzazione && (
                  <div className="grid md:grid-cols-2 gap-3">
                    <NumField label="Aliquota IRAP %"
                      value={settings.irap_aliquota} onChange={(v) => setSettings({ ...settings, irap_aliquota: v })}
                      tid="tc-irap-ord" step="0.1" suffix="%"
                      help="Standard 3,9%. Alcune regioni applicano maggiorazioni fino a +0,92%" />
                  </div>
                )}

                {!isForfettario && (
                  <div>
                    <div className="text-xs font-bold uppercase tracking-wider text-[color:var(--sp-text-muted)] mb-3">
                      IRPEF — Addizionali (%)
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                      <NumField label="Addizionale regionale"
                        value={settings.addizionale_regionale_pct} onChange={(v) => setSettings({ ...settings, addizionale_regionale_pct: v })}
                        tid="tc-add-regionale" step="0.01" suffix="%"
                        help="Variabile per regione (media Italia ≈ 1,73%)" />
                      <NumField label="Addizionale comunale"
                        value={settings.addizionale_comunale_pct} onChange={(v) => setSettings({ ...settings, addizionale_comunale_pct: v })}
                        tid="tc-add-comunale" step="0.01" suffix="%"
                        help="Variabile per comune (media ≈ 0,80%)" />
                      <NumField label="Detrazione forfettaria lav. autonomo (€)"
                        value={settings.detrazione_lav_autonomo} onChange={(v) => setSettings({ ...settings, detrazione_lav_autonomo: v })}
                        tid="tc-detrazione"
                        help="0 = ignora. Se hai detrazioni personali/familiari, indica il totale annuo" />
                    </div>
                  </div>
                )}

                {isSrl && (
                  <div>
                    <div className="text-xs font-bold uppercase tracking-wider text-[color:var(--sp-text-muted)] mb-3">SRL — Imposte societarie</div>
                    <div className="grid md:grid-cols-3 gap-3">
                      <NumField label="IRES %" value={settings.ires_aliquota} onChange={(v) => setSettings({ ...settings, ires_aliquota: v })}
                        tid="tc-ires" step="0.1" suffix="%" />
                      <NumField label="IRAP %" value={settings.irap_aliquota} onChange={(v) => setSettings({ ...settings, irap_aliquota: v })}
                        tid="tc-irap" step="0.1" suffix="%" help="Standard 3,9% (varia per regione)" />
                      <NumField label="IVA %" value={settings.iva_aliquota} onChange={(v) => setSettings({ ...settings, iva_aliquota: v })}
                        tid="tc-iva" step="0.5" suffix="%" />
                      <NumField label="Costo personale dipendente annuo (€)"
                        value={settings.srl_personale_annuo} onChange={(v) => setSettings({ ...settings, srl_personale_annuo: v })}
                        tid="tc-srl-personale"
                        help="Base IRAP = utile + costo personale. Se hai 3 dipendenti a €25.000/anno → 75.000" />
                    </div>
                    <div className="mt-4">
                      <div className="text-xs font-bold uppercase tracking-wider text-[color:var(--sp-text-muted)] mb-3">Distribuzione utili ai soci (dividendi)</div>
                      <div className="grid md:grid-cols-2 gap-3">
                        <NumField label="% utile netto distribuito ai soci"
                          value={settings.srl_distribuzione_utili_pct} onChange={(v) => setSettings({ ...settings, srl_distribuzione_utili_pct: v })}
                          tid="tc-srl-distrib" step="1" suffix="%"
                          help="0 = utile lasciato in società. 100 = tutto distribuito ai soci." />
                        <NumField label="Ritenuta dividendi %"
                          value={settings.srl_dividendi_aliquota} onChange={(v) => setSettings({ ...settings, srl_dividendi_aliquota: v })}
                          tid="tc-srl-divid" step="0.5" suffix="%"
                          help="26% ritenuta secca su dividendi (art. 27 DPR 600/1973)" />
                      </div>
                    </div>
                  </div>
                )}

                <div className="pt-3 border-t border-[color:var(--sp-border)]">
                  <button type="button" onClick={() => setSettings(DEFAULT_SETTINGS)}
                    data-testid="tc-settings-reset"
                    className="text-xs font-bold text-[color:var(--sp-text-muted)] hover:text-[color:var(--sp-danger)] inline-flex items-center gap-1">
                    <RotateCcw className="h-3 w-3" /> Ripristina default 2025
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Inputs */}
            <div className="sp-card p-6 space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h2 className="font-display text-xl font-black">Dati del periodo</h2>
                <div className="flex gap-1" data-testid="tc-period">
                  {[["month", "Mese"], ["quarter", "Trimestre"], ["year", "Anno"]].map(([k, l]) => (
                    <button key={k} data-testid={`tc-period-${k}`} onClick={() => setPeriod(k)}
                      className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${period === k ? "bg-[color:var(--sp-brand)] text-white" : "bg-[color:var(--sp-subtle)] text-[color:var(--sp-text-secondary)]"}`}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-bold">Incassi lordi (€)</label>
                <input data-testid="tc-revenue" type="number" min="0" step="0.01"
                  className="sp-input mt-1 text-lg font-mono"
                  value={revenue} onChange={(e) => setRevenue(e.target.value)} placeholder="0,00" />
              </div>
              <div>
                <label className="text-sm font-bold">Costi deducibili (€)</label>
                <input data-testid="tc-costs" type="number" min="0" step="0.01"
                  className="sp-input mt-1 text-lg font-mono"
                  value={costs} onChange={(e) => setCosts(e.target.value)} placeholder="0,00" />
                <div className="text-[11px] text-[color:var(--sp-text-muted)] mt-1">
                  {isForfettario
                    ? "Nel forfettario i costi non sono deducibili — l'imponibile è calcolato con coefficiente ATECO."
                    : "Importo dei costi/acquisti deducibili fiscalmente nel periodo."}
                </div>
              </div>
              <button data-testid="tc-autoload" type="button" onClick={fetchRealData} disabled={autoLoad}
                className="sp-btn-ghost text-xs inline-flex items-center gap-2 w-full justify-center">
                {autoLoad ? <Loader2 className="h-3 w-3 animate-spin" /> : <TrendingDown className="h-3 w-3" />}
                Precompila dai dati reali del periodo
              </button>
            </div>

            {/* Output */}
            <div className="sp-card p-6 space-y-4">
              <h2 className="font-display text-xl font-black">Stima da versare</h2>
              {result && (
                <>
                  {result.warnings && result.warnings.map((w, i) => (
                    <div key={i} data-testid={`tc-warning-${i}`}
                         className={`rounded-xl p-3 flex items-start gap-2 text-xs ${w.level === "danger" ? "bg-rose-50 border border-rose-200 text-rose-900" : "bg-amber-50 border border-amber-200 text-amber-900"}`}>
                      <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="font-bold">{w.title}</div>
                        <div className="mt-0.5 leading-snug">{w.desc}</div>
                      </div>
                    </div>
                  ))}
                  <div className="rounded-2xl bg-[color:var(--sp-brand)]/5 border border-[color:var(--sp-brand)]/20 p-4">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-[color:var(--sp-text-muted)]">
                      Totale imposte + contributi ({period === "month" ? "mese" : period === "quarter" ? "trimestre" : "anno"})
                    </div>
                    <div className="text-4xl font-display font-black text-[color:var(--sp-brand)] mt-1" data-testid="tc-total">
                      € {fmt(result.total)}
                    </div>
                    <div className="text-xs text-[color:var(--sp-text-muted)] mt-1">
                      Utile netto stimato: <strong className="text-emerald-700">€ {fmt(result.net)}</strong>
                      {" · "}Aliquota effettiva:{" "}
                      <strong className="text-[color:var(--sp-text)]">
                        {rev > 0 ? Math.round((result.total / rev) * 100) : 0}%
                      </strong>
                    </div>
                  </div>
                  <ul className="divide-y divide-[color:var(--sp-border)]">
                    {result.breakdown.map((b, i) => (
                      <li key={i} className={`py-2.5 flex items-baseline justify-between gap-4 ${b.mute ? "opacity-60" : ""}`}>
                        <div>
                          <div className="text-sm font-bold flex items-center gap-1.5">
                            {b.label}
                            {b.warn && <span title={b.warn} className="text-amber-600"><Info className="h-3 w-3" /></span>}
                          </div>
                          {b.desc && <div className="text-[10px] text-[color:var(--sp-text-muted)] mt-0.5">{b.desc}</div>}
                        </div>
                        <div className={`font-mono font-black text-sm whitespace-nowrap ${b.amount === 0 ? "text-[color:var(--sp-text-muted)]" : b.amount < 0 ? "text-emerald-700" : ""}`}>
                          € {fmt(b.amount)}
                        </div>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          </div>


          <div className="sp-card p-5 bg-slate-50/40 border-dashed">
            <div className="flex items-start gap-3">
              <Info className="h-4 w-4 text-[color:var(--sp-text-muted)] flex-shrink-0 mt-0.5" />
              <div className="text-[11px] text-[color:var(--sp-text-secondary)] leading-relaxed">
                <strong>Disclaimer.</strong> Stima indicativa. Non sostituisce il commercialista.
                Le aliquote effettive dipendono da variabili non completamente gestite qui (regime IVA
                trimestrale/mensile, ravvedimento, agevolazioni regionali, cuneo fiscale, deduzioni familiari).
                {isForfettario && " Nel forfettario i costi reali non sono deducibili."}
                {settings.artigiano_iscritto && " INPS artigiani modella minimale, primo e secondo massimale 2025."}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ------------------------------ SMALL UI ------------------------------ */
function NumField({ label, value, onChange, tid, help, step = "1", suffix }) {
  return (
    <div>
      <label className="text-[11px] font-bold text-[color:var(--sp-text-secondary)]">{label}</label>
      <div className="mt-1 relative">
        <input type="number" min="0" step={step} value={value}
          data-testid={tid}
          onChange={(e) => onChange(e.target.value === "" ? 0 : Number(e.target.value))}
          className={`sp-input font-mono text-sm ${suffix ? "pr-8" : ""}`} />
        {suffix && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[color:var(--sp-text-muted)] font-bold">{suffix}</span>
        )}
      </div>
      {help && <div className="text-[10px] text-[color:var(--sp-text-muted)] mt-0.5">{help}</div>}
    </div>
  );
}

/* ------------------------------ MATH ------------------------------ */
const REGIME_LABELS = {
  forfettario_5: "Forfettario · 5% (start-up)",
  forfettario_15: "Forfettario · 15%",
  ordinario_semplificato: "Ordinario semplificato (ditta individuale)",
  ordinario_srl: "Ordinario (SRL / società di capitali)",
};

function periodStart(p, now) {
  const d = new Date(now);
  if (p === "month") return new Date(d.getFullYear(), d.getMonth(), 1);
  if (p === "quarter") {
    const q = Math.floor(d.getMonth() / 3);
    return new Date(d.getFullYear(), q * 3, 1);
  }
  return new Date(d.getFullYear(), 0, 1);
}

function fmt(v) {
  return (Number(v) || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * Compute annual INPS artigiani contribution given the annual taxable base.
 * Returns granular breakdown so the UI can explain "why".
 */
function computeInpsArtigiani(imponibileAnnuo, s) {
  const minimale = Number(s.inps_minimale_annuo) || 0;
  const fisso = Number(s.inps_fisso_annuo) || 0;
  const aliquota = (Number(s.inps_aliquota) || 0) / 100;
  const primoMassimale = Number(s.inps_massimale_annuo) || 0;
  const aliqExtra = (Number(s.inps_aliquota_extra) || 0) / 100;
  const secondoMassimale = Number(s.inps_secondo_massimale) || primoMassimale;

  let totale = fisso; // sempre dovuto, anche se reddito < minimale
  let eccedenzaBase = 0, contribEcc = 0;
  let extraBase = 0, contribExtra = 0;
  let sopraSecondoBase = 0; // sopra secondo massimale non si paga IVS

  if (imponibileAnnuo > minimale) {
    // fascia tra minimale e primo massimale → aliquota base
    eccedenzaBase = Math.min(imponibileAnnuo, primoMassimale) - minimale;
    if (eccedenzaBase > 0) {
      contribEcc = eccedenzaBase * aliquota;
      totale += contribEcc;
    }
    if (imponibileAnnuo > primoMassimale) {
      // fascia tra primo e secondo massimale → aliquota extra
      extraBase = Math.min(imponibileAnnuo, secondoMassimale) - primoMassimale;
      if (extraBase > 0) {
        contribExtra = extraBase * aliqExtra;
        totale += contribExtra;
      }
      if (imponibileAnnuo > secondoMassimale) {
        sopraSecondoBase = imponibileAnnuo - secondoMassimale;
        // sopra secondo massimale nessun contributo IVS ulteriore
      }
    }
  }

  if (s.forfettario_riduzione_35) {
    totale *= 0.65;
  }
  return {
    totale, fisso, eccedenzaBase, contribEcc, extraBase, contribExtra, sopraSecondoBase,
    riduzione35: !!s.forfettario_riduzione_35,
  };
}

/** IRPEF a scaglioni 2024/2025 (3 scaglioni). Ritorna imposta annuale. */
function irpefScaglioniAnnual(annualImponibile) {
  const b1 = Math.min(annualImponibile, 28000) * 0.23;
  const b2 = Math.max(0, Math.min(annualImponibile, 50000) - 28000) * 0.35;
  const b3 = Math.max(0, annualImponibile - 50000) * 0.43;
  return b1 + b2 + b3;
}

function computeTaxes(regime, revenue, costs, coef, period, s) {
  const factor = period === "month" ? 12 : period === "quarter" ? 4 : 1;
  const revenueAnnual = revenue * factor;
  const costsAnnual = costs * factor;
  const artigianoOn = !!s.artigiano_iscritto;

  // ============ FORFETTARIO ============
  if (regime === "forfettario_5" || regime === "forfettario_15") {
    const rate = regime === "forfettario_5" ? 0.05 : 0.15;
    const imponibileAnnuo = revenueAnnual * coef;
    const inps = artigianoOn ? computeInpsArtigiani(imponibileAnnuo, s) : { totale: 0 };
    // Contributi INPS deducibili dall'imponibile (art. 1 c. 64 L. 190/2014)
    const baseImpostaSost = s.forfettario_deduci_inps
      ? Math.max(0, imponibileAnnuo - inps.totale)
      : imponibileAnnuo;
    const impostaSostAnnua = baseImpostaSost * rate;
    const totaleAnnuo = impostaSostAnnua + inps.totale;

    const inpsDesc = artigianoOn
      ? `Fisso € ${fmt(inps.fisso / factor)} sempre dovuto${inps.eccedenzaBase > 0 ? ` · +${s.inps_aliquota}% su € ${fmt(inps.eccedenzaBase / factor)} eccedenza` : ""}${inps.contribExtra > 0 ? ` · +${s.inps_aliquota_extra}% oltre massimale` : ""}${inps.riduzione35 ? " · sconto 35% applicato" : ""}`
      : "Non iscritto alla Gestione Artigiani (impostazione disattivata)";

    const overThreshold = revenueAnnual > (s.forfettario_soglia_ricavi || 85000);

    return {
      total: totaleAnnuo / factor,
      net: revenue - totaleAnnuo / factor,
      warnings: overThreshold ? [{
        level: "danger",
        title: `Superata soglia forfettario € ${fmt(s.forfettario_soglia_ricavi || 85000)}`,
        desc: `Ricavi annualizzati stimati € ${fmt(revenueAnnual)}. Dal prossimo anno esci automaticamente dal regime forfettario e passi all'ordinario. Se superi € 100.000 esci immediatamente durante l'anno.`,
      }] : [],
      breakdown: [
        ...(factor > 1 ? [{ label: `Base annualizzata × ${factor}`, desc: "Le imposte sono calcolate sull'anno intero e poi divise per periodo.", amount: revenueAnnual / factor, mute: true }] : []),
        { label: "Imponibile fiscale lordo", desc: `Ricavi × coefficiente ATECO ${Math.round(coef * 100)}%`, amount: imponibileAnnuo / factor },
        ...(artigianoOn && s.forfettario_deduci_inps ? [{
          label: "− INPS deducibile",
          desc: "I contributi previdenziali obbligatori si sottraggono all'imponibile prima dell'imposta sostitutiva",
          amount: -inps.totale / factor,
          mute: false,
        }] : []),
        ...(artigianoOn && s.forfettario_deduci_inps ? [{
          label: "Imponibile netto",
          desc: "Base effettiva imposta sostitutiva",
          amount: baseImpostaSost / factor,
          mute: true,
        }] : []),
        { label: `Imposta sostitutiva ${Math.round(rate * 100)}%`, desc: s.forfettario_deduci_inps ? "Sull'imponibile netto (dopo deduzione INPS)" : "Sull'imponibile lordo", amount: impostaSostAnnua / factor },
        ...(artigianoOn ? [{ label: "INPS Gestione Artigiani", desc: inpsDesc, amount: inps.totale / factor }] : []),
        { label: "IVA a debito", desc: "Regime forfettario: esente da IVA", amount: 0 },
        { label: "IRAP", desc: "Non dovuta nel forfettario", amount: 0 },
        { label: "Addizionali IRPEF", desc: "Non dovute (l'imposta sostitutiva le assorbe)", amount: 0 },
      ],
    };
  }

  // ============ ORDINARIO SEMPLIFICATO (ditta individuale) ============
  if (regime === "ordinario_semplificato") {
    const margineAnnuo = Math.max(0, revenueAnnual - costsAnnual);
    const ivaAnnua = Math.max(0, (revenueAnnual - costsAnnual) * (s.iva_aliquota / 100));
    // INPS artigiani deducibile dall'imponibile IRPEF (semplificazione)
    const inps = artigianoOn ? computeInpsArtigiani(margineAnnuo, s) : { totale: 0 };
    const baseIrpef = Math.max(0, margineAnnuo - inps.totale - (Number(s.detrazione_lav_autonomo) || 0));
    const irpef = irpefScaglioniAnnual(baseIrpef);
    const addizionali = baseIrpef * ((Number(s.addizionale_regionale_pct) + Number(s.addizionale_comunale_pct)) / 100);
    // IRAP: dovuta solo con autonoma organizzazione (dipendenti o beni strumentali sopra soglia)
    const irapDovuta = !!s.ord_autonoma_organizzazione;
    const irap = irapDovuta ? margineAnnuo * (Number(s.irap_aliquota) / 100) : 0;
    const totaleAnnuo = ivaAnnua + irpef + addizionali + inps.totale + irap;

    const inpsDesc = artigianoOn
      ? `Fisso € ${fmt(inps.fisso / factor)} + ${s.inps_aliquota}% eccedenza oltre € ${fmt(s.inps_minimale_annuo / factor)}${inps.contribExtra > 0 ? ` + ${s.inps_aliquota_extra}% oltre massimale` : ""}`
      : "Non iscritto alla Gestione Artigiani";

    return {
      total: totaleAnnuo / factor,
      net: (revenueAnnual - costsAnnual - totaleAnnuo) / factor,
      breakdown: [
        ...(factor > 1 ? [{ label: `Base annualizzata × ${factor}`, amount: revenueAnnual / factor, mute: true }] : []),
        { label: "Margine (ricavi − costi)", desc: "Base per IRPEF e INPS", amount: margineAnnuo / factor },
        { label: `IVA netta ${s.iva_aliquota}%`, desc: "IVA su vendite − IVA su acquisti", amount: ivaAnnua / factor },
        ...(artigianoOn ? [{ label: "INPS Gestione Artigiani", desc: inpsDesc, amount: inps.totale / factor }] : []),
        { label: "IRPEF a scaglioni", desc: `23% ≤ 28k · 35% 28-50k · 43% > 50k (su base imponibile netta INPS)`, amount: irpef / factor },
        { label: "Addizionali IRPEF", desc: `${Number(s.addizionale_regionale_pct).toFixed(2)}% regionale + ${Number(s.addizionale_comunale_pct).toFixed(2)}% comunale`, amount: addizionali / factor },
        { label: `IRAP ${irapDovuta ? s.irap_aliquota + "%" : ""}`,
          desc: irapDovuta
            ? `Dovuta con autonoma organizzazione (dipendenti/beni strumentali sopra soglia). Aliquota ${s.irap_aliquota}%`
            : "Esente senza autonoma organizzazione. Se hai dipendenti sopra soglia, abilita la voce nelle impostazioni.",
          amount: irap / factor,
          warn: irapDovuta ? undefined : "Se hai dipendenti abilita IRAP dalle impostazioni contributive avanzate" },
      ],
    };
  }

  // ============ SRL ============
  if (regime === "ordinario_srl") {
    const utileAnnuo = Math.max(0, revenueAnnual - costsAnnual);
    const iva = Math.max(0, (revenueAnnual - costsAnnual) * (s.iva_aliquota / 100));
    const ires = utileAnnuo * ((Number(s.ires_aliquota) || 24) / 100);
    // IRAP base = valore produzione ≈ utile + costo personale dipendente + interessi (semplificazione)
    const personaleAnnuo = (Number(s.srl_personale_annuo) || 0) * factor; // se input mensile normalizzo
    // NB: personale_annuo è già annuale nelle settings, non moltiplicare. Interpreto come annuale diretto.
    const irapBase = utileAnnuo + (Number(s.srl_personale_annuo) || 0);
    const irap = irapBase * ((Number(s.irap_aliquota) || 3.9) / 100);
    // Dividendi: utile residuo × distribuzione % × ritenuta 26%
    const utileNettoSocieta = Math.max(0, utileAnnuo - ires - irap);
    const utileDistribuito = utileNettoSocieta * ((Number(s.srl_distribuzione_utili_pct) || 0) / 100);
    const ritenutaDividendi = utileDistribuito * ((Number(s.srl_dividendi_aliquota) || 26) / 100);
    const totaleAnnuo = iva + ires + irap + ritenutaDividendi;

    return {
      total: totaleAnnuo / factor,
      net: (utileNettoSocieta - ritenutaDividendi) / factor,
      breakdown: [
        ...(factor > 1 ? [{ label: `Base annualizzata × ${factor}`, amount: revenueAnnual / factor, mute: true }] : []),
        { label: "Utile lordo (ricavi − costi)", desc: "Base IRES", amount: utileAnnuo / factor },
        { label: `IVA netta ${s.iva_aliquota}%`, desc: "Su vendite − acquisti", amount: iva / factor },
        { label: `IRES ${s.ires_aliquota}%`, desc: "Sull'utile lordo", amount: ires / factor },
        { label: `IRAP ${s.irap_aliquota}%`,
          desc: `Base IRAP = utile € ${fmt(utileAnnuo / factor)}${(s.srl_personale_annuo || 0) > 0 ? ` + costo personale annuo € ${fmt((s.srl_personale_annuo || 0) / factor)}` : ""}. Aliquota varia per regione (nelle impostazioni).`,
          amount: irap / factor },
        ...(utileDistribuito > 0 ? [{
          label: `Ritenuta dividendi al socio ${s.srl_dividendi_aliquota}%`,
          desc: `Su € ${fmt(utileDistribuito / factor)} distribuiti (${s.srl_distribuzione_utili_pct}% dell'utile netto)`,
          amount: ritenutaDividendi / factor,
        }] : []),
        { label: "INPS soci lavoratori", desc: "I soci lavoratori sono iscritti INPS separatamente — non incluso qui.", amount: 0, mute: true },
      ],
    };
  }

  return { total: 0, net: revenue, breakdown: [] };
}
